import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { NavigationConfig, NavigationEvent } from '../navigation-link/interfaces/navigation.interfaces';
import { BusinessPartnerNavigationService } from '../../services/business-partner-navigation.service';
import {
  BusinessPartnerNavigationData,
  BusinessPartnerQueryParams,
  BusinessPartnerNavigationEvent,
  BusinessPartnerDisplayMode,
  BusinessPartnerNavigationConfig
} from './interfaces/business-partner-navigation.interfaces';

/**
 * Business Partner specific navigation component
 * Uses NavigationLinkComponent internally and provides BP-specific functionality
 * Handles display modes, validation, and BP-specific navigation logic
 */
@Component({
  selector: 'app-business-partner-navigation',
  standalone: true,
  imports: [
    CommonModule,
    NavigationLinkComponent
  ],
  templateUrl: './business-partner-navigation.component.html',
  styleUrls: ['./business-partner-navigation.component.scss']
})
export class BusinessPartnerNavigationComponent {
  /** Business partner data for navigation */
  @Input() businessPartner: BusinessPartnerNavigationData;

  /** Display mode for the navigation link text */
  @Input() displayMode: BusinessPartnerDisplayMode = 'code';

  /** Custom query parameters to include in navigation */
  @Input() customQueryParams: BusinessPartnerQueryParams = {};

  /** Whether to open navigation in new tab */
  @Input() openInNewTab: boolean = true;

  /** Custom CSS classes for styling */
  @Input() customClass: string = '';

  /** Whether to show the ship icon */
  @Input() showIcon: boolean = true;

  /** Position of the icon */
  @Input() iconPosition: 'left' | 'right' = 'right';

  /** Custom icon (defaults to ship icon) */
  @Input() icon: string = 'heroicons_outline:rocket-launch';

  /** Whether to show the text */
  @Input() showText: boolean = true;

  /** Which parts are clickable */
  @Input() interactionMode: 'both' | 'text-only' | 'icon-only' = 'both';

  /** Custom tooltip text (if empty, uses service default) */
  @Input() customTooltipText?: string;

  /** Event emitted when navigation occurs */
  @Output() businessPartnerNavigate = new EventEmitter<BusinessPartnerNavigationEvent>();

  constructor(private bpNavService: BusinessPartnerNavigationService) {}

  /**
   * Check if the business partner is navigable
   */
  get isNavigable(): boolean {
    return this.bpNavService.isBusinessPartnerNavigable(this.businessPartner);
  }

  /**
   * Get display text based on display mode and available data
   */
  get displayText(): string {
    if (!this.businessPartner) return '-';

    const code = this.businessPartner.externalKeys?.code || this.businessPartner.code;
    const name = this.businessPartner.name;

    switch (this.displayMode) {
      case 'code':
        return code || '-';

      case 'name':
        return name || '-';

      case 'both':
        if (code && name) {
          return `${code} - ${name}`;
        }
        return code || name || '-';

      default:
        return code || '-';
    }
  }

  /**
   * Get tooltip text for the navigation link
   */
  get tooltipText(): string {
    if (!this.isNavigable) return '';

    return this.customTooltipText || this.bpNavService.getNavigationDescription();
  }

  /**
   * Get navigation configuration for the core NavigationLinkComponent
   */
  get navigationConfig(): NavigationConfig<BusinessPartnerNavigationData, BusinessPartnerQueryParams> {
    return {
      data: this.businessPartner,
      url: ['master', 'business-partner', 'business-partner'],
      queryParams: this.customQueryParams,
      openInNewTab: this.openInNewTab
    };
  }

  /**
   * Handle navigation event from NavigationLinkComponent
   */
  handleNavigation(navigationEvent: NavigationEvent<BusinessPartnerNavigationData, BusinessPartnerQueryParams>): void {
    if (!this.isNavigable) return;

    // Create business partner specific navigation event
    const bpNavigationEvent: BusinessPartnerNavigationEvent = {
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp,
      source: 'general'
    };

    // Emit business partner navigation event
    this.businessPartnerNavigate.emit(bpNavigationEvent);

    // Execute navigation through service
    this.bpNavService.navigateToBusinessPartnerDetail(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab
    );
  }

  /**
   * Get display text with fallback handling
   */
  private getDisplayTextWithFallback(): string {
    const text = this.displayText;
    return text === '-' ? '' : text;
  }

  /**
   * Check if business partner has valid display data
   */
  hasValidDisplayData(): boolean {
    return this.getDisplayTextWithFallback().length > 0;
  }

  /**
   * Get CSS classes for the container
   */
  getContainerClasses(): string {
    const baseClasses = 'business-partner-navigation';
    const customClasses = this.customClass || '';
    const navigableClasses = this.isNavigable ? 'navigable' : 'non-navigable';

    return [baseClasses, navigableClasses, customClasses]
      .filter(cls => cls.length > 0)
      .join(' ');
  }
}